import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import {
  getEffectiveHeartRateData,
  parseHeartRateData,
} from '../../../shared/hr-data';
import type {
  DeterministicAnalysisResult,
  SessionRepairStatus,
} from '../types';
import { deriveSessionRepairStatus } from './deriveSessionRepairStatus';
import { runDeterministicAnalysis } from './runDeterministicAnalysis';

type ConfigurationStatusInput = Pick<
  DeviceConfigurationApi,
  'hrData' | 'hrData_repaired'
> & {
  hasHrData?: boolean;
  hasHrDataRepaired?: boolean;
};

export const deriveRepairStatusFromConfiguration = (
  data: ConfigurationStatusInput
): SessionRepairStatus =>
  deriveSessionRepairFieldsFromConfiguration(data).repairStatus;

export const deriveSessionRepairFieldsFromConfiguration = (
  data: ConfigurationStatusInput
): {
  repairStatus: SessionRepairStatus;
  duplicateCount?: number;
  gapCount?: number;
} => {
  const originalEvents = parseHeartRateData(data.hrData);
  const hasHrData = data.hasHrData ?? originalEvents.length > 0;
  const hasHrDataRepaired =
    data.hasHrDataRepaired ?? Boolean(data.hrData_repaired);

  if (!hasHrData) {
    return { repairStatus: 'no_hr_data' };
  }

  if (hasHrDataRepaired) {
    return { repairStatus: 'repaired_perfect' };
  }

  const analysis =
    originalEvents.length > 0
      ? runDeterministicAnalysis(originalEvents)
      : undefined;

  return {
    repairStatus: deriveSessionRepairStatus({
      hasHrData,
      hasHrDataRepaired,
      analysis,
    }),
    duplicateCount: analysis?.duplicateCount,
    gapCount: analysis?.gapCount,
  };
};

export type ConfigurationScanState = {
  analysis?: DeterministicAnalysisResult;
  isAnalyzing: boolean;
  analysisFailed: boolean;
  eventCount?: number;
};

export const buildScanStateFromConfiguration = (
  data: DeviceConfigurationApi,
  lite: { hasHrData: boolean; hasHrDataRepaired: boolean }
): ConfigurationScanState => {
  const originalEvents = parseHeartRateData(data.hrData);
  const effectiveEvents = getEffectiveHeartRateData(data);
  const needsAnalysis = lite.hasHrData && !lite.hasHrDataRepaired;

  return {
    analysis:
      needsAnalysis && originalEvents.length > 0
        ? runDeterministicAnalysis(originalEvents)
        : undefined,
    isAnalyzing: false,
    analysisFailed: false,
    eventCount: effectiveEvents.length,
  };
};
